import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { useQueryClient } from 'react-query';
import Grid from '@material-ui/core/Grid';
import Tooltip from '@material-ui/core/Tooltip';
import withStyles from '@material-ui/core/styles/withStyles';
import { Typography } from '@material-ui/core';
import CollectionKeys from '../../../hooks/molecules/collection-keys';
import useAssociationReports from '../../../hooks/association-reports-hooks';
import { useAlerts } from '../../../hooks/alerts-hooks';
import axiosClient from '../../../http/axios-client';
import AppButtonMinimal from '../../basic/AppButtonMinimal';
import { getTheme } from '../../../theme';
import AppTable from '../../basic/AppTable';
import { filterTypes } from '../../basic/AdvancedFilters';

const DisabledColumn = ({ value }) => {
  return (
    <div style={ {
      margin: 'auto',
      backgroundColor: value ? getTheme().palette.error.main : getTheme().palette.success.main,
      height: '20px',
      width: '20px',
      borderRadius: '100%',
    } }
    />
  );
};

const InfoTooltip = withStyles((theme) => {
  return {
    tooltip: {
      backgroundColor: '#f5f5f9',
      color: 'rgba(0, 0, 0, 0.87)',
      maxWidth: 220,
      fontSize: theme.typography.pxToRem(12),
      border: '1px solid #dadde9',
    },
  };
})(Tooltip);

const VariantsColumn = ({ value }) => {
  if (!value || !Array.isArray(value) || !value.length) {
    return <p>No variants</p>;
  }

  return (
    <div>
      {
        value.map((v) => {
          if (!v || !v.name) {
            return <div />;
          }

          const {
            name,
            assembly,
            allele_string: alleleString,
            risk_allele: riskAllele,
          } = v;

          const infos = [
            {
              label: 'Name',
              value: name,
            },
            {
              label: 'Assembly',
              value: assembly,
            },
            {
              label: 'Allele',
              value: alleleString,
            },
            {
              label: 'Risk allele',
              value: riskAllele,
            },
          ];

          return (
            <InfoTooltip
              key={ v.id }
              title={ (
                <Grid container>
                  {
                    infos.map(({ value: infoValue, label: infoLabel }) => {
                      return (
                        <Grid
                          key={ infoLabel }
                          container
                          item
                          xs={ 12 }
                          justify='space-between'
                          alignItems='center'
                          style={ { padding: getTheme().spacing(1, 0) } }
                        >
                          <Grid item>
                            <Typography variant='subtitle2' style={ { fontWeight: 'bold' } }>
                              {infoLabel}
                            </Typography>
                          </Grid>
                          <Grid item>
                            <Typography>
                              {infoValue}
                            </Typography>
                          </Grid>
                        </Grid>
                      );
                    })
                  }

                </Grid>
              ) }
            >
              <p>
                {v.name}
                {' '}
                -
                {' '}
                {v.risk_allele}
              </p>
            </InfoTooltip>
          );
        })
      }
    </div>
  );
};

const tableColumns = [
  { label: 'On / Off', field: 'disabled', customRenderer: DisabledColumn },
  { label: 'Variants', field: 'variants', customRenderer: VariantsColumn },
  { label: 'p_value_log', field: 'p_value_log' },
  { label: 'beta', field: 'beta' },
  { label: 'or', field: 'or' },
  { label: 'ci_lower', field: 'ci_lower' },
  { label: 'ci_upper', field: 'ci_upper' },
  { label: 'risk_allele_freq', field: 'risk_allele_freq' },

].map((c, idx) => {
  return {
    id: idx,
    ...c,
  };
});

const AssociationReportsDetails = (
  {
    version,
  },
) => {
  const [ filterQuery, setFilterQuery ] = useState({});
  const [ selected, setSelected ] = useState([]);
  const [ ctaLoading, setCTALoading ] = useState(false);
  const { alertSuccess, alertError } = useAlerts();
  const queryClient = useQueryClient();

  useEffect(() => {
    setSelected([]);
  }, [ version ]);

  const {
    associationReports,
    associationReportsLoading,
    pagination,
  } = useAssociationReports({
    filterQuery: {
      ...filterQuery,
      'version.id_eq': version.id,
    },
  });

  const {
    associationReports: allEnabledAssocitionReports,
    // associationReportsLoading: enabledLoading,
  } = useAssociationReports({
    filterQuery: {
      ...filterQuery,
      _limit: -1,
      // disabled: false,
      'version.id_eq': version.id,
    },
  });

  const selectedAssociationReports = useMemo(() => {
    return associationReports?.filter((ar) => { return selected.includes(ar.id); });
  }, [ selected, associationReports ]);

  const handleRunSimulation = useCallback(() => {
    // const assumingVersionId = selectedAssociationReports[0].version.id;
    //  const assumingVersionId = enabledAssociationReports[0].version.id;
    // selectedAssociationReports
    return queryClient.executeMutation({
      mutationKey: [ CollectionKeys.SIMULATIONS ],
      mutationFn: () => {
        return axiosClient.post('edag/prescoring/simulate', {
          version: version.id,
          reports: 0 === selected?.length ? allEnabledAssocitionReports?.map((ar) => { return ar.id; }) : selected,
        }).catch((err) => {
          throw err;
        });
      },
      onSuccess() {
        alertSuccess('Successfully requested simulation. Please wait until the simulation is completed');
        return queryClient.invalidateQueries([ CollectionKeys.SIMULATIONS ]);
      },
      onError() {
        alertError('Unable to run simulation. Please try again in a moment');
      },
    });
  }, [ selectedAssociationReports, allEnabledAssocitionReports ]); //  selectedAssociationReports

  const handleBulkAction = (associationReportIds, action) => {
    const actionPromises = associationReportIds.map((tId) => {
      return Promise.resolve()
        .then(() => {
          return axiosClient.put(`${ CollectionKeys.ASSOCIATION_REPORTS }/${ tId }`, { disabled: action });
        });
    });

    return queryClient.executeMutation({
      mutationKey: [ CollectionKeys.ASSOCIATION_REPORTS ],
      mutationFn: () => {
        return Promise.all(actionPromises);
      },
      onSuccess() {
        alertSuccess(`Successfully ${ action ? 'disabled' : 'enabled' } ${ actionPromises.length } association report(s)`);
        return queryClient.invalidateQueries([ CollectionKeys.ASSOCIATION_REPORTS ]);
      },

    });
  };

  return (
    <Grid container spacing={ 3 }>
      <Grid item xs={ 12 } container justify='space-between'>
        <Grid item>
          <AppButtonMinimal
            text='Clear'
            onClick={ () => { return setSelected([]); } }
            disabled={ !selected.length || ctaLoading }
          />
        </Grid>
        <Grid item>
          <Grid container spacing={ 2 }>
            <Grid item>
              <AppButtonMinimal
                text='Select All'
                color={ getTheme().palette.success.main }
                disabled={ ctaLoading }// || !selected.length
                onClick={ () => {
                  setSelected(allEnabledAssocitionReports?.map((ar) => { return ar.id; }));
                } }
              />
            </Grid>
            <Grid item>
              <AppButtonMinimal
                text='Run simulation'
                color={ getTheme().palette.warning.main }
                disabled={ ctaLoading }// || !selected.length
                onClick={ () => {
                  return Promise.resolve()
                    .then(() => {
                      return setCTALoading(true);
                    })
                    .then(() => {
                      return handleRunSimulation(selected);// selected
                    })
                    .then(() => {
                      return setSelected([]);
                    })
                    .finally(() => {
                      setCTALoading(false);
                    });
                } }
              />
            </Grid>
            <Grid item>
              <AppButtonMinimal
                text='Enable selected'
                color={ getTheme().palette.success.main }
                disabled={ ctaLoading || !selected.length }
                onClick={ () => {
                  return Promise.resolve()
                    .then(() => {
                      return setCTALoading(true);
                    })
                    .then(() => {
                      return handleBulkAction(selected, false);
                    })
                    .then(() => {
                      return setSelected([]);
                    })
                    .finally(() => {
                      setCTALoading(false);
                    });
                } }
              />
            </Grid>
            <Grid item>
              <AppButtonMinimal
                text='Disable selected'
                color={ getTheme().palette.error.main }
                disabled={ ctaLoading || !selected.length }
                onClick={ () => {
                  return Promise.resolve()
                    .then(() => {
                      return setCTALoading(true);
                    })
                    .then(() => {
                      return handleBulkAction(selected, true);
                    })
                    .then(() => {
                      return setSelected([]);
                    })
                    .finally(() => {
                      setCTALoading(false);
                    });
                } }
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={ 12 }>
        <AppTable
          columns={ tableColumns }
          data={ associationReports }
          paginationProps={ pagination }
          loading={ associationReportsLoading }
          advancedFiltersProps={ {
            properties:
                    [
                      {
                        value: 'disabled',
                        label: 'disabled',
                        type: filterTypes.CATEGORY,
                        options: [
                          { value: false, label: 'FALSE' },
                          { value: true, label: 'TRUE' },
                        ],
                      },
                      {
                        value: 'p_value_log',
                        label: 'p_value_log',
                        type: filterTypes.NUMERIC,
                      },
                      {
                        value: 'beta',
                        label: 'beta',
                        type: filterTypes.NUMERIC,
                      },
                      {
                        value: 'or',
                        label: 'or',
                        type: filterTypes.NUMERIC,
                      },
                      {
                        value: 'ci_lower',
                        label: 'ci_lower',
                        type: filterTypes.NUMERIC,
                      },
                      {
                        value: 'ci_upper',
                        label: 'ci_upper',
                        type: filterTypes.NUMERIC,
                      },
                      {
                        value: 'risk_allele_freq',
                        label: 'risk_allele_freq',
                        type: filterTypes.NUMERIC,
                      },
                    ],
            initialAndFilterOptions: [],
            onQueryChange: setFilterQuery,
          } }
          selectionProps={ { setSelected, selected } }
        />
      </Grid>
    </Grid>
  );
};

export default AssociationReportsDetails;
